import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export const ThemeProvider = ({ children }) => {
	const [theme, setTheme] = useState(() => {
		// Check localStorage first, then system preference, default to light
		if (typeof window !== 'undefined') {
			const savedTheme = localStorage.getItem('theme');
			if (savedTheme) {
				return savedTheme;
			}
			// Check system preference
			if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
				return 'dark';
			}
		}
		return 'light';
	});
	const [mounted, setMounted] = useState(false);

	// Apply theme immediately to prevent flashing
	useEffect(() => {
		const root = document.documentElement;
		
		// Remove both classes first
		root.classList.remove('light', 'dark');
		// Add current theme class
		root.classList.add(theme);
		
		// Update data-theme attribute for debugging
		root.setAttribute('data-theme', theme);
		
		// Save to localStorage
		localStorage.setItem('theme', theme);
		
		// Update meta theme-color for mobile browsers
		const metaThemeColor = document.querySelector('meta[name="theme-color"]');
		if (metaThemeColor) {
			metaThemeColor.setAttribute('content', theme === 'dark' ? '#0f1221' : '#ffffff');
		}
		
		console.log('ThemeContext: Theme applied immediately:', theme);
	}, [theme]); // Include theme in dependencies

	useEffect(() => {
		setMounted(true);
	}, []);

	// Listen for system theme changes
	useEffect(() => {
		if (!mounted) return;
		
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (e) => {
			// Only auto-switch if user hasn't manually set a preference
			if (!localStorage.getItem('theme')) {
				console.log('ThemeContext: System theme changed to:', e.matches ? 'dark' : 'light');
				setTheme(e.matches ? 'dark' : 'light');
			}
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [mounted]);

	const toggleTheme = () => {
		const newTheme = theme === 'light' ? 'dark' : 'light';
		console.log('ThemeContext: Toggling theme from', theme, 'to', newTheme);
		setTheme(newTheme);
	};

	const value = {
		theme,
		toggleTheme,
		isDark: theme === 'dark',
		mounted
	};

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
};
